import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardPasswordGate from '../analytics-dashboard/components/DashboardPasswordGate';
import {
  fetchBookings,
  updateBookingStatus,
  deleteBooking,
  insertAdminAction,
  insertBookingLog,
  fetchAdminActions,
  isAuthenticated,
} from '../../services/apiService';


const STATUS_COLORS = {
  confirmed: { bg: 'rgba(5,150,105,0.1)', color: '#059669', label: 'Bestätigt' },
  rescheduled: { bg: 'rgba(245,158,11,0.1)', color: '#d97706', label: 'Verschoben' },
  cancelled: { bg: 'rgba(239,68,68,0.1)', color: '#dc2626', label: 'Storniert' },
  lead: { bg: 'rgba(29,78,216,0.1)', color: '#1D4ED8', label: '📞 FB-Lead · Rückruf' },
};

const ACTION_LABELS = {
  reschedule: { icon: '📅', label: 'Verschoben', color: '#d97706' },
  cancel: { icon: '❌', label: 'Storniert', color: '#dc2626' },
  resend_confirmation: { icon: '✉️', label: 'Bestätigung erneut gesendet', color: '#3E4954' },
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [bookings, setBookings] = useState([]);
  const [showBerater, setShowBerater] = useState(false);
  const [beraterConfig, setBeraterConfig] = useState(null);

  useEffect(() => {
    if (showBerater && !beraterConfig) {
      fetch('/api/admin/berater', { headers: { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` } })
        .then(r => r.json()).then(setBeraterConfig).catch(() => {});
    }
  }, [showBerater]);
  const [loading, setLoading] = useState(isAuthenticated());
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modal, setModal] = useState(null); // 'reschedule' | 'cancel' | 'resend' | 'audit'
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  // Reschedule form state
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const loadBookings = useCallback(async () => {
    setLoading(true);
    const data = await fetchBookings();
    setBookings(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authenticated) loadBookings();
  }, [authenticated, loadBookings]);

  const filtered = bookings?.filter(b => {
    const q = search?.toLowerCase();
    const matchSearch = !q ||
      `${b?.vorname} ${b?.nachname}`?.toLowerCase()?.includes(q) ||
      b?.email?.toLowerCase()?.includes(q) ||
      b?.booking_ref?.toLowerCase()?.includes(q);
    const matchStatus = !filterStatus || (b?.status || 'confirmed') === filterStatus;
    const matchChannel = !filterChannel || b?.channel === filterChannel || (!b?.channel && filterChannel === 'direct');
    return matchSearch && matchStatus && matchChannel;
  });

  const openModal = async (booking, type) => {
    setSelectedBooking(booking);
    setModal(type);
    setActionMsg(null);
    setRescheduleDate(booking?.date || '');
    setRescheduleTime(booking?.time || '');
    setCancelReason('');
    if (type === 'audit') {
      setAuditLoading(true);
      const logs = await fetchAdminActions(booking?.booking_ref);
      setAuditLogs(logs);
      setAuditLoading(false);
    }
  };

  const closeModal = () => {
    setModal(null);
    setSelectedBooking(null);
    setActionMsg(null);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      setActionMsg({ type: 'error', text: 'Bitte Datum und Uhrzeit angeben.' });
      return;
    }
    setActionLoading(true);
    const ref = selectedBooking?.booking_ref;
    const oldDate = selectedBooking?.date;
    const oldTime = selectedBooking?.time;
    await updateBookingStatus(ref, 'rescheduled', {
      rescheduled_date: rescheduleDate,
      rescheduled_time: rescheduleTime,
      date: rescheduleDate,
      time: rescheduleTime,
    });
    await insertAdminAction(ref, 'reschedule', {
      old_date: oldDate,
      old_time: oldTime,
      new_date: rescheduleDate,
      new_time: rescheduleTime,
    });
    await insertBookingLog(ref, 'booking_rescheduled', {
      old_date: oldDate,
      old_time: oldTime,
      new_date: rescheduleDate,
      new_time: rescheduleTime,
    });
    setActionLoading(false);
    setActionMsg({ type: 'success', text: 'Termin erfolgreich verschoben.' });
    await loadBookings();
    setTimeout(closeModal, 1200);
  };

  const handleCancel = async () => {
    setActionLoading(true);
    const ref = selectedBooking?.booking_ref;
    await updateBookingStatus(ref, 'cancelled', {
      cancelled_at: new Date()?.toISOString(),
      cancelled_reason: cancelReason,
    });
    await insertAdminAction(ref, 'cancel', { reason: cancelReason });
    await insertBookingLog(ref, 'booking_cancelled', { reason: cancelReason });
    setActionLoading(false);
    setActionMsg({ type: 'success', text: 'Buchung storniert.' });
    await loadBookings();
    setTimeout(closeModal, 1200);
  };

  const handleExport = async () => {
    const token = sessionStorage.getItem('zba_admin_token');
    const res = await fetch('/api/admin/export', {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) { alert('Export fehlgeschlagen'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().split('T')[0];
    a.download = `ZBA-Buchungen-${today}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    setActionLoading(true);
    const ref = selectedBooking?.booking_ref;
    const result = await deleteBooking(ref);
    setActionLoading(false);
    if (result?.deleted) {
      const msg = result?.outlookDeleted
        ? 'Buchung + Outlook-Event gelöscht.'
        : result?.outlookError
          ? `Buchung gelöscht. Outlook-Fehler: ${result.outlookError}`
          : 'Buchung gelöscht (kein Outlook-Event vorhanden).';
      setActionMsg({ type: 'success', text: msg });
      await loadBookings();
      setTimeout(closeModal, 1500);
    } else {
      setActionMsg({ type: 'error', text: result?.error || 'Löschen fehlgeschlagen.' });
    }
  };

  const handleResend = async () => {
    setActionLoading(true);
    const ref = selectedBooking?.booking_ref;
    await insertAdminAction(ref, 'resend_confirmation', {
      email: selectedBooking?.email,
      resent_at: new Date()?.toISOString(),
    });
    await insertBookingLog(ref, 'confirmation_resent', { email: selectedBooking?.email });
    setActionLoading(false);
    setActionMsg({ type: 'success', text: 'Bestätigung erneut gesendet (geloggt).' });
    setTimeout(closeModal, 1400);
  };

  const formatDate = (d) => {
    if (!d) return '–';
    const p = d?.split('-'); if (p?.length === 3) return `${p[2]}.${p[1]}.${p[0]}`; return d;
  };

  const formatTs = (ts) => {
    if (!ts) return '–';
    try { return new Date(ts)?.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  const getStatus = (b) => b?.status || 'confirmed';

  if (!authenticated) {
    return <DashboardPasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'var(--font-ui)' }}>
      {/* Header */}
      <header style={{
        background: 'white', borderBottom: '1px solid #dde1e7',
        padding: '0 24px', height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 4px rgba(62,73,84,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7a8d', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, padding: '6px 10px', borderRadius: '8px', transition: 'all 150ms' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f5f6f8'; e.currentTarget.style.color = '#1e2b38'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#6b7a8d'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Dashboard
          </button>
          <div style={{ width: '1px', height: '20px', background: '#dde1e7' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', background: '#3E4954', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e2b38', lineHeight: 1.1 }}>Buchungsverwaltung</div>
              <div style={{ fontSize: '11px', color: '#6b7a8d' }}>Admin — Verschieben, Stornieren, Bestätigung</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/settings')} style={{ padding: '7px 14px', borderRadius: '10px', border: '1.5px solid #dde1e7', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#3E4954', transition: 'all 150ms' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#3E4954'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#3E4954'; }}
          >Einstellungen</button>
        </div>
      </header>
      <div style={{ maxWidth: '1300px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Filters */}
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #dde1e7', padding: '20px 24px', marginBottom: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7a8d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text" placeholder="Name, E-Mail oder Ref suchen…" value={search}
              onChange={e => setSearch(e?.target?.value)}
              style={{ width: '100%', height: '38px', paddingLeft: '36px', paddingRight: '12px', border: '1.5px solid #dde1e7', borderRadius: '10px', fontSize: '13px', color: '#1e2b38', outline: 'none', fontFamily: 'var(--font-ui)' }}
            />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
            style={{ height: '38px', padding: '0 12px', border: '1.5px solid #dde1e7', borderRadius: '10px', fontSize: '13px', color: '#1e2b38', background: 'white', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            <option value="">Alle Status</option>
            <option value="confirmed">Bestätigt</option>
            <option value="rescheduled">Verschoben</option>
            <option value="cancelled">Storniert</option>
            <option value="lead">📞 FB-Leads (Rückruf)</option>
          </select>
          <select value={filterChannel} onChange={e => setFilterChannel(e?.target?.value)}
            style={{ height: '38px', padding: '0 12px', border: '1.5px solid #dde1e7', borderRadius: '10px', fontSize: '13px', color: '#1e2b38', background: 'white', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            <option value="">Alle Kanäle</option>
            <option value="direct">🔗 Direkt / Webinar</option>
            <option value="fb">👍 Facebook (Termin)</option>
            <option value="fb_lead">📞 Facebook-Lead (Rückruf)</option>
            <option value="tg">✈️ Telegram</option>
          </select>
          <a href="/admin/leads"
            style={{ height: '38px', padding: '0 16px', background: '#1D4ED8', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)', textDecoration: 'none' }}>
            📞 FB-Leads
          </a>
          <a href="/crm"
            style={{ height: '38px', padding: '0 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)', textDecoration: 'none' }}>
            📊 CRM & Deals
          </a>
          <button onClick={() => setShowBerater(!showBerater)}
            style={{ height: '38px', padding: '0 16px', background: showBerater ? '#3E4954' : '#F0F6FB', color: showBerater ? 'white' : '#3E4954', border: '1.5px solid #3E4954', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)' }}>
            👥 Berater
          </button>
          <a href="/admin/step2work"
            style={{ height: '38px', padding: '0 16px', background: '#F0F6FB', color: '#3E4954', border: '1.5px solid #3E4954', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)', textDecoration: 'none' }}>
            🤝 Step2Work
          </a>
          <a href="/events-admin"
            style={{ height: '38px', padding: '0 16px', background: '#FF4500', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)', textDecoration: 'none' }}>
            🎪 Events
          </a>
          <button
            onClick={handleExport}
            style={{ height: '38px', padding: '0 16px', background: '#3E4954', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)' }}
            onMouseEnter={e => e.currentTarget.style.background = '#2d3840'}
            onMouseLeave={e => e.currentTarget.style.background = '#3E4954'}
          >
            📥 Excel
          </button>
          <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7a8d', fontWeight: 600 }}>
            {filtered?.length} Buchung{filtered?.length !== 1 ? 'en' : ''}
          </div>
        </div>

        {/* Table */}
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #dde1e7', overflow: 'hidden', boxShadow: '0 4px 6px rgba(62,73,84,0.04)' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#6b7a8d', fontSize: '14px' }}>⏳ Buchungen werden geladen…</div>
          ) : filtered?.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#6b7a8d', fontSize: '14px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
              Keine Buchungen gefunden.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {/* Berater config panel */}
              {showBerater && beraterConfig && (
                <div style={{ margin: '0 0 20px', padding: '20px 24px', background: '#fff', borderRadius: '14px', border: '1px solid #D0DFE8' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#3E4954', marginBottom: '16px', fontFamily: 'var(--font-ui)' }}>
                    Berater-Zuordnung
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ padding: '16px', background: '#F0F6FB', borderRadius: '12px', border: '1px solid #D0E5F5' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#3E4954', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                        Interessenten-Termine
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B8299', marginBottom: '10px' }}>/tg · /fb · /events · Hauptformular</div>
                      {beraterConfig.regular.map(b => (
                        <div key={b.email} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#fff', borderRadius: '8px', marginBottom: '6px', border: '1px solid #D0DFE8' }}>
                          <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#3E4954', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{b.initials}</span>
                          <div><div style={{ fontSize: '13px', fontWeight: 600 }}>{b.name}</div><div style={{ fontSize: '11px', color: '#6B8299' }}>{b.email}</div></div>
                        </div>
                      ))}
                      <div style={{ fontSize: '11px', color: '#6B8299', marginTop: '8px' }}>Vor-Ort: {beraterConfig.durations.regular.onsite} Min · Telefon: {beraterConfig.durations.regular.phone} Min</div>
                    </div>
                    <div style={{ padding: '16px', background: '#F5F0FF', borderRadius: '12px', border: '1px solid #DDD6FE' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#5B21B6', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                        Institutionen-Termine
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B8299', marginBottom: '10px' }}>/institutionen</div>
                      {beraterConfig.institutionen.map(b => (
                        <div key={b.email} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#fff', borderRadius: '8px', marginBottom: '6px', border: '1px solid #DDD6FE' }}>
                          <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#5B21B6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{b.initials}</span>
                          <div><div style={{ fontSize: '13px', fontWeight: 600 }}>{b.name}</div><div style={{ fontSize: '11px', color: '#6B8299' }}>{b.email}</div></div>
                        </div>
                      ))}
                      <div style={{ fontSize: '11px', color: '#6B8299', marginTop: '8px' }}>Vor-Ort: {beraterConfig.durations.institutionen.onsite} Min · Telefon: {beraterConfig.durations.institutionen.phone} Min</div>
                    </div>
                  </div>
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f6f8', borderBottom: '1px solid #dde1e7' }}>
                    {['Ref', 'Name', 'E-Mail', 'Datum', 'Uhrzeit', 'Berater', 'Status', 'Aktionen']?.map(col => (
                      <th key={col} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#6b7a8d', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map((b, i) => {
                    const st = getStatus(b);
                    const sc = STATUS_COLORS?.[st] || STATUS_COLORS?.confirmed;
                    return (
                      <tr key={b?.id} style={{ borderBottom: i < filtered?.length - 1 ? '1px solid #f0f1f3' : 'none', transition: 'background 150ms' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '13px 16px', fontSize: '12px', fontWeight: 700, color: '#3E4954', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{b?.booking_ref}</td>
                        <td style={{ padding: '13px 16px', fontSize: '14px', fontWeight: 600, color: '#1e2b38', whiteSpace: 'nowrap' }}>{b?.vorname} {b?.nachname}</td>
                        <td style={{ padding: '13px 16px', fontSize: '13px', color: '#6b7a8d' }}>{b?.email}</td>
                        <td style={{ padding: '13px 16px', fontSize: '13px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{formatDate(b?.date)}</td>
                        <td style={{ padding: '13px 16px', fontSize: '13px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{st === 'lead' ? <a href={`tel:${(b?.telefon||'').replace(/[^0-9+]/g,'')}`} style={{ color: '#1D4ED8', fontWeight: 600, textDecoration: 'none' }}>{b?.telefon}</a> : (b?.time || '–')}</td>
                        <td style={{ padding: '13px 16px', fontSize: '13px', color: '#1e2b38', whiteSpace: 'nowrap' }}>{b?.berater_name || '–'}</td>
                        <td style={{ padding: '13px 16px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px', background: sc?.bg, color: sc?.color, whiteSpace: 'nowrap' }}>{sc?.label}</span>
                        </td>
                        <td style={{ padding: '13px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
                            {st !== 'cancelled' && st !== 'lead' && (
                              <>
                                <ActionBtn icon="📅" label="Verschieben" color="#d97706" onClick={() => openModal(b, 'reschedule')} />
                                <ActionBtn icon="✉️" label="Bestätigung" color="#3E4954" onClick={() => openModal(b, 'resend')} />
                                <ActionBtn icon="❌" label="Stornieren" color="#dc2626" onClick={() => openModal(b, 'cancel')} />
                              </>
                            )}
                            <ActionBtn icon="🗑️" label="Löschen" color="#991b1b" onClick={() => openModal(b, 'delete')} />
                            <ActionBtn icon="📋" label="Protokoll" color="#6b7a8d" onClick={() => openModal(b, 'audit')} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Modal */}
      {modal && selectedBooking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          onClick={e => { if (e?.target === e?.currentTarget) closeModal(); }}
        >
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            {/* Modal Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #dde1e7', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#3E4954' }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'white' }}>
                  {modal === 'reschedule' && '📅 Termin verschieben'}
                  {modal === 'cancel' && '❌ Buchung stornieren'}
                  {modal === 'delete' && '🗑️ Buchung komplett löschen'}
                  {modal === 'resend' && '✉️ Bestätigung erneut senden'}
                  {modal === 'audit' && '📋 Aktions-Protokoll'}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', marginTop: '2px' }}>
                  {selectedBooking?.vorname} {selectedBooking?.nachname} — {selectedBooking?.booking_ref}
                </div>
              </div>
              <button onClick={closeModal} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', color: 'white', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Booking info strip */}
              {modal !== 'audit' && (
                <div style={{ background: '#f5f6f8', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#3E4954' }}>
                  <strong>{selectedBooking?.date}</strong> um <strong>{selectedBooking?.time}</strong> — {selectedBooking?.berater_name}<br />
                  <span style={{ color: '#6b7a8d' }}>{selectedBooking?.email}</span>
                </div>
              )}

              {/* Reschedule Form */}
              {modal === 'reschedule' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Neues Datum</label>
                    <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e?.target?.value)}
                      style={{ width: '100%', height: '40px', padding: '0 12px', border: '1.5px solid #dde1e7', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-ui)', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Neue Uhrzeit</label>
                    <input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e?.target?.value)}
                      style={{ width: '100%', height: '40px', padding: '0 12px', border: '1.5px solid #dde1e7', borderRadius: '10px', fontSize: '14px', fontFamily: 'var(--font-ui)', outline: 'none' }} />
                  </div>
                  {actionMsg && <MsgBanner msg={actionMsg} />}
                  <button onClick={handleReschedule} disabled={actionLoading}
                    style={{ height: '42px', background: '#d97706', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? 'Wird gespeichert…' : 'Termin verschieben'}
                  </button>
                </div>
              )}

              {/* Cancel Form */}
              {modal === 'cancel' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '6px' }}>Stornierungsgrund (optional)</label>
                    <textarea value={cancelReason} onChange={e => setCancelReason(e?.target?.value)}
                      placeholder="z.B. Kunde hat abgesagt…"
                      rows={3}
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #dde1e7', borderRadius: '10px', fontSize: '13px', fontFamily: 'var(--font-ui)', resize: 'vertical', outline: 'none' }} />
                  </div>
                  {actionMsg && <MsgBanner msg={actionMsg} />}
                  <button onClick={handleCancel} disabled={actionLoading}
                    style={{ height: '42px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? 'Wird storniert…' : 'Buchung stornieren'}
                  </button>
                </div>
              )}

              {/* Delete — removes from DB + Outlook */}
              {modal === 'delete' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px' }}>
                    <p style={{ fontSize: '13px', color: '#991b1b', margin: 0, fontWeight: 600 }}>
                      Buchung <strong>{selectedBooking?.booking_ref}</strong> wird komplett gelöscht:
                    </p>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px', fontSize: '13px', color: '#991b1b' }}>
                      <li>Eintrag aus der Datenbank</li>
                      <li>Alle zugehörigen Logs</li>
                      {selectedBooking?.outlook_event_id && <li>Outlook-Kalenderevent bei {selectedBooking?.berater_name || selectedBooking?.berater_email}</li>}
                    </ul>
                  </div>
                  <p style={{ fontSize: '13px', color: '#6b7a8d', margin: 0 }}>
                    Diese Aktion kann nicht rückgängig gemacht werden.
                  </p>
                  {actionMsg && <MsgBanner msg={actionMsg} />}
                  <button onClick={handleDelete} disabled={actionLoading}
                    style={{ height: '42px', background: '#991b1b', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? 'Wird gelöscht…' : '🗑️ Endgültig löschen'}
                  </button>
                </div>
              )}

              {/* Resend Confirmation */}
              {modal === 'resend' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <p style={{ fontSize: '14px', color: '#3E4954', margin: 0 }}>
                    Bestätigungs-E-Mail erneut an <strong>{selectedBooking?.email}</strong> senden?
                  </p>
                  {actionMsg && <MsgBanner msg={actionMsg} />}
                  <button onClick={handleResend} disabled={actionLoading}
                    style={{ height: '42px', background: '#3E4954', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.7 : 1 }}>
                    {actionLoading ? 'Wird gesendet…' : 'Bestätigung senden'}
                  </button>
                </div>
              )}

              {/* Audit Log */}
              {modal === 'audit' && (
                <div>
                  {auditLoading ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#6b7a8d', fontSize: '13px' }}>⏳ Protokoll wird geladen…</div>
                  ) : auditLogs?.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#6b7a8d', fontSize: '13px' }}>Noch keine Admin-Aktionen für diese Buchung.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '360px', overflowY: 'auto' }}>
                      {auditLogs?.map(log => {
                        const ac = ACTION_LABELS?.[log?.action_type] || { icon: '🔧', label: log?.action_type, color: '#6b7a8d' };
                        return (
                          <div key={log?.id} style={{ background: '#f5f6f8', borderRadius: '10px', padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '18px', flexShrink: 0 }}>{ac?.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: ac?.color }}>{ac?.label}</div>
                              <div style={{ fontSize: '11px', color: '#6b7a8d', marginTop: '2px' }}>{formatTs(log?.performed_at)}</div>
                              {log?.details && Object.keys(log?.details)?.length > 0 && (
                                <div style={{ marginTop: '6px', fontSize: '11px', color: '#3E4954', background: 'white', borderRadius: '6px', padding: '6px 8px', border: '1px solid #dde1e7' }}>
                                  {Object.entries(log?.details)?.map(([k, v]) => (
                                    <div key={k}><span style={{ color: '#6b7a8d' }}>{k}:</span> {String(v)}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionBtn = ({ icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    title={label}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '5px 10px', borderRadius: '8px',
      border: `1.5px solid ${color}20`,
      background: `${color}10`,
      color, cursor: 'pointer', fontSize: '11px', fontWeight: 700,
      whiteSpace: 'nowrap', transition: 'all 150ms',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = 'white'; }}
    onMouseLeave={e => { e.currentTarget.style.background = `${color}10`; e.currentTarget.style.color = color; }}
  >
    <span>{icon}</span> {label}
  </button>
);

const MsgBanner = ({ msg }) => (
  <div style={{
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
    background: msg?.type === 'success' ? 'rgba(5,150,105,0.1)' : 'rgba(239,68,68,0.1)',
    color: msg?.type === 'success' ? '#059669' : '#dc2626',
    border: `1px solid ${msg?.type === 'success' ? 'rgba(5,150,105,0.2)' : 'rgba(239,68,68,0.2)'}`,
  }}>
    {msg?.text}
  </div>
);

export default AdminBookings;
